import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DashboardData, DashboardFilters } from '../types/dashboard.types';
import { HealthPanelCard } from './components';
import { useHealthAnalysis } from './hooks/useHealthAnalysis';
import { useEngineStartup } from './hooks/useEngineStartup';
import { EngineStartupTimeline } from './components/EngineStartupTimeline';
import { useTranslation } from '../../../i18n';

interface HealthPanelTabProps {
  data: DashboardData;
  filters: DashboardFilters;
  onFilterChange: (filters: Partial<DashboardFilters>) => void;
}

export const HealthPanelTab: React.FC<HealthPanelTabProps> = ({
  data: _data,
  filters,
  onFilterChange: _onFilterChange,
}) => {
  // Required by common tab interface but not used in this tab
  void _data;
  void _onFilterChange;

  const { t } = useTranslation();

  // State for dropdown expansion
  const [engineStartupExpanded, setEngineStartupExpanded] = useState(false);
  const [performanceExpanded, setPerformanceExpanded] = useState(false);
  const [coolingExpanded, setCoolingExpanded] = useState(false);
  const [batteriesExpanded, setBatteriesExpanded] = useState(false);
  const [fuelSystemExpanded, setFuelSystemExpanded] = useState(false);
  const [liftSystemExpanded, setLiftSystemExpanded] = useState(false);

  // Engine startup hook
  const engineStartup = useEngineStartup(filters.selectedSerial);

  // Health analysis hooks
  const performanceAnalysis = useHealthAnalysis(filters.selectedSerial, 'performance');
  const coolingAnalysis = useHealthAnalysis(filters.selectedSerial, 'cooling');
  const batteriesAnalysis = useHealthAnalysis(filters.selectedSerial, 'batteries');
  const fuelSystemAnalysis = useHealthAnalysis(filters.selectedSerial, 'fuelSystem');
  const liftSystemAnalysis = useHealthAnalysis(filters.selectedSerial, 'liftSystem');

  if (!filters.selectedSerial) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          {t('healthPanel.selectDrone')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Engine Startup Health Panel */}
      <Box sx={{ mb: 2 }}>
        <HealthPanelCard
          title={t('healthPanel.engineStartup')}
          expanded={engineStartupExpanded}
          onToggleExpanded={() => setEngineStartupExpanded(!engineStartupExpanded)}
          loading={engineStartup.loading}
          error={engineStartup.error || undefined}
        >
          <EngineStartupTimeline flights={engineStartup.flights} />
        </HealthPanelCard>
      </Box>

      {/* Performance Health Panel */}
      <Box sx={{ mb: 2 }}>
        <HealthPanelCard
          title={t('healthPanel.performance')}
          expanded={performanceExpanded}
          onToggleExpanded={() => setPerformanceExpanded(!performanceExpanded)}
          analyses={performanceAnalysis.analyses}
          loading={performanceAnalysis.loading}
          error={performanceAnalysis.error || undefined}
          infoContent={t('healthPanel.performanceInfo')}
        />
      </Box>

      {/* Cooling Health Panel */}
      <Box sx={{ mb: 2 }}>
        <HealthPanelCard
          title={t('healthPanel.cooling')}
          expanded={coolingExpanded}
          onToggleExpanded={() => setCoolingExpanded(!coolingExpanded)}
          analyses={coolingAnalysis.analyses}
          loading={coolingAnalysis.loading}
          error={coolingAnalysis.error || undefined}
          infoContent={t('healthPanel.coolingInfo')}
        />
      </Box>

      {/* Batteries Health Panel */}
      <Box sx={{ mb: 2 }}>
        <HealthPanelCard
          title={t('healthPanel.batteries')}
          expanded={batteriesExpanded}
          onToggleExpanded={() => setBatteriesExpanded(!batteriesExpanded)}
          analyses={batteriesAnalysis.analyses}
          loading={batteriesAnalysis.loading}
          error={batteriesAnalysis.error || undefined}
        />
      </Box>

      {/* Fuel System Health Panel */}
      <Box sx={{ mb: 2 }}>
        <HealthPanelCard
          title={t('healthPanel.fuelSystem')}
          expanded={fuelSystemExpanded}
          onToggleExpanded={() => setFuelSystemExpanded(!fuelSystemExpanded)}
          analyses={fuelSystemAnalysis.analyses}
          loading={fuelSystemAnalysis.loading}
          error={fuelSystemAnalysis.error || undefined}
        />
      </Box>

      {/* Lift System Health Panel */}
      <Box sx={{ mb: 2 }}>
        <HealthPanelCard
          title={t('healthPanel.liftSystem')}
          expanded={liftSystemExpanded}
          onToggleExpanded={() => setLiftSystemExpanded(!liftSystemExpanded)}
          analyses={liftSystemAnalysis.analyses}
          loading={liftSystemAnalysis.loading}
          error={liftSystemAnalysis.error || undefined}
        />
      </Box>
    </Box>
  );
};
